import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  selectedVariantId,
  productData,
  changeVariant,
} from "../store/product-store";

/**
 * Reactive variant selector using radio buttons (for products with <5 variants).
 *
 * Renders radio button fieldsets for each product option and syncs with the
 * product store. When a radio is selected, calls changeVariant() which updates
 * the global state and URL.
 *
 * Usage in Liquid:
 * ```liquid
 * <variant-radios
 *   data-section="{{ section.id }}"
 *   data-url="{{ product.url }}"
 * >
 *   {%- for option in product.options_with_values -%}
 *     <fieldset>...</fieldset>
 *   {%- endfor -%}
 * </variant-radios>
 * ```
 */
@customElement("variant-radios")
export class VariantRadios extends LitElement {
  @state() private _options: string[] = [];
  @state() private _selectedVariantId: number | null = null;
  private _isUserInteraction = false;

  constructor() {
    super();

    // Subscribe to selected variant changes
    effect(() => {
      this._selectedVariantId = selectedVariantId.value;
      // Only update radios if this wasn't triggered by user interaction
      if (!this._isUserInteraction) {
        this.updateOptionsFromSelectedVariant();
      }
      this._isUserInteraction = false;
    });
  }

  // Light DOM — no shadow root (allows global CSS and Tailwind)
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    // Set up radio change listeners
    this.addEventListener("change", this._onRadioChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("change", this._onRadioChange);
  }

  /**
   * Handle radio input change events.
   * Reads all checked radios to build the option values array,
   * then finds the matching variant and updates the store.
   */
  private _onRadioChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    if (target.type !== "radio") return;

    // Mark this as user interaction to prevent the effect from overwriting
    this._isUserInteraction = true;

    // Collect all checked radio values (one per fieldset/option)
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));
    const selectedOptions = fieldsets.map((fieldset) => {
      const checkedRadio = fieldset.querySelector(
        'input[type="radio"]:checked'
      ) as HTMLInputElement;
      return checkedRadio?.value ?? "";
    });

    // Find the variant matching these option values
    const variant = this._findVariantByOptions(selectedOptions);
    if (variant) {
      changeVariant(variant.id);
    }
  };

  /**
   * Find a variant by matching its option values.
   * Example: ["Red", "Large"] → variant with option1=Red, option2=Large
   */
  private _findVariantByOptions(options: string[]): any {
    if (!productData.value) return null;

    return productData.value.variants.find((variant: any) => {
      // Shopify variants have option1, option2, option3
      return options.every((opt, idx) => {
        const optionKey = `option${idx + 1}`;
        return variant[optionKey] === opt;
      });
    });
  }

  /**
   * When the selected variant changes (from another component or initial load),
   * update which radios are checked.
   */
  private updateOptionsFromSelectedVariant() {
    if (!productData.value || !this._selectedVariantId) return;

    const variant = productData.value.variants.find(
      (v: any) => v.id === this._selectedVariantId
    );
    if (!variant) return;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Update radio checked states to match variant options
      const fieldsets = Array.from(this.querySelectorAll("fieldset"));
      fieldsets.forEach((fieldset, idx) => {
        const optionKey = `option${idx + 1}`;
        const optionValue = variant[optionKey];
        if (!optionValue) return;

        const radios = fieldset.querySelectorAll(
          'input[type="radio"]'
        ) as NodeListOf<HTMLInputElement>;
        radios.forEach((radio) => {
          radio.checked = radio.value === optionValue;
        });
      });
    });
  }

  render() {
    // The Liquid template already renders the radio HTML structure,
    // so we just need to render a slot to display it.
    // The component's main job is to handle change events and sync state.
    return html`<slot></slot>`;
  }
}
